import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { TapeBadge } from './TapeBadge';
export function BeforeAfterGallery() {
  // Composite before/after files (single image shows both states)
  const caseFiles = [{
    img: 'https://cdn.magicpatterns.com/uploads/f8ewoB63sQceej4rWhEA9u/Screenshot_2026-05-27_at_5.00.14_PM.png',
    label: 'WEBER SPIRIT',
    location: 'SCOTTSDALE, AZ',
    note: 'Sat through three summers. Now it shines.',
    rotation: -2
  }, {
    img: 'https://cdn.magicpatterns.com/uploads/9fc1QHBXJvWDXnF1yB5o3w/Screenshot_2026-05-27_at_5.00.38_PM.png',
    label: 'WEBER GENESIS',
    location: 'GILBERT, AZ',
    note: 'Mirror-finish lid. Looks brand new.',
    rotation: 2
  }, {
    img: 'https://cdn.magicpatterns.com/uploads/5nPYdECvSfFCxYaUs2aJeh/Screenshot_2026-05-27_at_5.00.21_PM.png',
    label: 'CAST IRON GRATES',
    location: 'CHANDLER, AZ',
    note: 'Scraped, scrubbed, and seasoned.',
    rotation: -1.5
  }, {
    img: 'https://cdn.magicpatterns.com/uploads/jzFffL5TaiCbXVQ11s1j5j/Screenshot_2026-05-27_at_5.00.28_PM.png',
    label: 'FLAVORIZER BARS',
    location: 'MESA, AZ',
    note: 'Caked carbon → bare metal.',
    rotation: 1.5
  }];
  // Paired before/after — two separate photos shown side by side
  const pairs = [{
    before: 'https://cdn.magicpatterns.com/uploads/r4WiABUSLkz8jowN7a7aB6/Screenshot_2026-05-27_at_5.01.02_PM.png',
    after: 'https://cdn.magicpatterns.com/uploads/tbhMTHPfcpk4dkYSXMHNY6/Screenshot_2026-05-27_at_5.01.24_PM.png',
    label: 'SUMMERSET 36"',
    location: 'PARADISE VALLEY, AZ',
    note: 'Built-in beast. Stainless brought back to life.',
    rotation: -1.5
  }, {
    before: 'https://cdn.magicpatterns.com/uploads/wW4aifZr2i6TzeKwDed4g4/Screenshot_2026-05-27_at_5.01.09_PM.png',
    after: 'https://cdn.magicpatterns.com/uploads/319NGtCBANqA4aqvzCa43Q/Screenshot_2026-05-27_at_5.01.16_PM.png',
    label: 'CAST IRON DEEP CLEAN',
    location: 'TEMPE, AZ',
    note: 'Last cook? Six months ago. Not anymore.',
    rotation: 1.5
  }];
  // Standalone "in the wild" shots — raw befores, no after to pair with
  const wildShots = [{
    img: 'https://cdn.magicpatterns.com/uploads/7fwpeJk9TvASXveg2mpRtU/Screenshot_2026-05-27_at_5.00.51_PM.png',
    caption: 'Found like this. We took the job anyway.',
    rotation: -3
  }];
  return <section id="gallery" className="relative overflow-hidden bg-otter-black py-24 text-otter-bone" data-id="element-35">
      {/* Arizona ambient backdrop */}
      <div className="absolute inset-0 bg-cover bg-center opacity-[0.08]" style={{
      backgroundImage: "url('https://images.unsplash.com/photo-1518733057094-95b53143d2a7?w=1600&q=80')"
    }} data-id="element-36" />
      <div className="absolute inset-0 bg-gradient-to-b from-otter-black via-otter-black/80 to-otter-black" data-id="element-37" />

      <div className="container relative z-10 mx-auto px-4" data-id="element-38">
        <div className="mb-20 flex flex-col items-center text-center" data-id="element-39">
          <TapeBadge text="REAL CUSTOMERS · REAL GRIME" color="cyan" rotation={-2} className="mb-6" data-id="element-40" />
          <h2 className="font-display text-6xl leading-[0.85] tracking-tighter sm:text-8xl lg:text-[9rem]" data-id="element-41">
            THE <span className="text-otter-cyan" data-id="element-42">GLOW-UP</span>
          </h2>
          <p className="mt-6 max-w-2xl font-body text-lg text-otter-bone/70 sm:text-xl" data-id="element-43">
            We don't just wipe it down — we perform an exorcism on your BBQ.
            Straight from Arizona backyards. No filters. No fakes.
          </p>
        </div>

        {/* Featured paired before/after — side by side */}
        <div className="mx-auto mb-20 max-w-6xl space-y-16" data-id="element-44">
          {pairs.map((pair, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6
        }} className="relative" style={{
          transform: `rotate(${pair.rotation}deg)`
        }} data-id="element-45">
              {/* Header strip */}
              <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b-2 border-dashed border-otter-cyan/40 pb-3" data-id="element-46">
                <div className="flex items-baseline gap-4" data-id="element-47">
                  <span className="font-display text-xs tracking-[0.3em] text-otter-cyan" data-id="element-48">
                    CASE #{String(idx + 1).padStart(3, '0')}
                  </span>
                  <h3 className="font-display text-3xl tracking-wider text-otter-bone sm:text-4xl" data-id="element-49">
                    {pair.label}
                  </h3>
                </div>
                <span className="font-body text-xs font-bold tracking-widest text-otter-bone/50" data-id="element-50">
                  {pair.location}
                </span>
              </div>

              <div className="grid gap-6 sm:grid-cols-2" data-id="element-51">
                {/* Before */}
                <div className="group relative bg-otter-bone p-3 pb-4 shadow-brutal" data-id="element-52">
                  <div className="absolute -top-3 left-6 h-7 w-20 bg-otter-blaze/80" style={{
                clipPath: 'polygon(4% 0, 96% 6%, 100% 94%, 0 100%)'
              }} data-id="element-53" />
                  <div className="overflow-hidden bg-otter-black" data-id="element-54">
                    <img src={pair.before} alt={`${pair.label} before cleaning in ${pair.location}`} className="h-[360px] w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0 sm:h-[420px]" loading="lazy" data-id="element-55" />
                  </div>
                  <p className="mt-3 font-display text-lg tracking-widest text-otter-blaze" data-id="element-56">
                    BEFORE
                  </p>
                </div>

                {/* After */}
                <div className="group relative bg-otter-bone p-3 pb-4 shadow-brutal-cyan" data-id="element-57">
                  <div className="absolute -top-3 right-6 h-7 w-20 bg-otter-cyan/80" style={{
                clipPath: 'polygon(4% 0, 96% 6%, 100% 94%, 0 100%)'
              }} data-id="element-58" />
                  <div className="overflow-hidden bg-otter-black" data-id="element-59">
                    <img src={pair.after} alt={`${pair.label} after cleaning in ${pair.location}`} className="h-[360px] w-full object-cover transition-transform duration-500 group-hover:scale-[1.02] sm:h-[420px]" loading="lazy" data-id="element-60" />
                  </div>
                  <p className="mt-3 text-right font-display text-lg tracking-widest text-otter-cyan" data-id="element-61">
                    AFTER
                  </p>
                </div>
              </div>

              <p className="mt-4 font-accent text-xl text-otter-bone/80" data-id="element-62">
                "{pair.note}"
              </p>
            </motion.div>)}
        </div>

        {/* Divider */}
        <div className="my-16 flex items-center gap-4" data-id="element-63">
          <div className="h-px flex-1 bg-otter-cyan/30" data-id="element-64" />
          <span className="font-display text-sm tracking-[0.4em] text-otter-cyan" data-id="element-65">
            MORE FROM THE FIELD
          </span>
          <div className="h-px flex-1 bg-otter-cyan/30" data-id="element-66" />
        </div>

        {/* Composite case file grid */}
        <div className="mx-auto grid max-w-6xl gap-12 sm:grid-cols-2" data-id="element-67">
          {caseFiles.map((file, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.5,
          delay: idx * 0.08
        }} whileHover={{
          rotate: 0,
          scale: 1.02,
          y: -8
        }} className="group relative bg-otter-bone p-4 pb-6 shadow-brutal-cyan" style={{
          transform: `rotate(${file.rotation}deg)`
        }} data-id="element-68">
              {/* Tape strip top */}
              <div className="absolute -top-4 left-1/2 h-8 w-24 -translate-x-1/2 bg-otter-cyan/70 backdrop-blur-sm" style={{
            clipPath: 'polygon(4% 0, 96% 6%, 100% 94%, 0 100%)'
          }} data-id="element-69" />

              {/* File number stamp */}
              <div className="absolute -right-3 top-3 z-10 rotate-[8deg] border-2 border-otter-blaze bg-otter-bone px-2 py-1 font-display text-xs tracking-widest text-otter-blaze" data-id="element-70">
                FILE #00{idx + 1}
              </div>

              <div className="relative overflow-hidden border-2 border-otter-black/20 bg-otter-black" data-id="element-71">
                <img src={file.img} alt={`${file.label} before and after cleaning in ${file.location}`} className="h-auto w-full object-contain" loading="lazy" data-id="element-72" />
              </div>

              <div className="mt-4 flex items-end justify-between gap-3 px-1" data-id="element-73">
                <div data-id="element-74">
                  <p className="font-display text-2xl tracking-wider text-otter-black" data-id="element-75">
                    {file.label}
                  </p>
                  <p className="font-body text-xs font-bold tracking-widest text-otter-black/50" data-id="element-76">
                    {file.location}
                  </p>
                </div>
                <p className="max-w-[55%] text-right font-accent text-base leading-tight text-otter-black/80" data-id="element-77">
                  "{file.note}"
                </p>
              </div>
            </motion.div>)}
        </div>

        {/* In the wild — standalone shots */}
        <div className="mx-auto mt-16 grid max-w-3xl gap-6 sm:grid-cols-2 sm:items-end" data-id="element-78">
          {wildShots.map((shot, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="relative bg-otter-bone p-3 pb-5 shadow-brutal" style={{
          transform: `rotate(${shot.rotation}deg)`
        }} data-id="element-79">
              <div className="absolute -top-3 left-1/2 h-7 w-20 -translate-x-1/2 bg-otter-blaze/70" style={{
            clipPath: 'polygon(4% 0, 96% 6%, 100% 94%, 0 100%)'
          }} data-id="element-80" />
              <img src={shot.img} alt={shot.caption} className="h-[280px] w-full object-cover" loading="lazy" data-id="element-81" />
              <p className="mt-3 font-accent text-base leading-tight text-otter-black/80" data-id="element-82">
                "{shot.caption}"
              </p>
            </motion.div>)}

          {/* Companion copy */}
          <div className="self-center px-2" data-id="element-83">
            <p className="font-display text-3xl leading-tight tracking-wider text-otter-bone" data-id="element-84">
              SOME GRILLS NEED{' '}
              <span className="text-otter-cyan" data-id="element-85">A MIRACLE.</span>
            </p>
            <p className="mt-3 font-body text-base text-otter-bone/60" data-id="element-86">
              We've never turned one down. Send us a pic — we'll tell you
              straight up if it's saveable.
            </p>
          </div>
        </div>

        <p className="mt-16 text-center font-accent text-2xl text-otter-cyan" data-id="element-87">
          ...and 500+ more we're too busy to post.
        </p>
      </div>
    </section>;
}